import { AlertTriangle, CheckCircle2, Loader2, UploadCloud } from 'lucide-react'
import { useCallback, useEffect, useRef, useState } from 'react'
import { type DropEvent, type FileRejection, useDropzone } from 'react-dropzone'
import { Button } from '@/components/ui/button'
import { Progress } from '@/components/ui/progress'
import { api } from '@/lib/api'
import { cn } from '@/lib/utils'

type UploadStatus = 'idle' | 'generating-url' | 'uploading' | 'confirming' | 'success' | 'error'

interface DirectVideoUploaderProps {
  lessonId: string
  onUploadSuccess?: (result: { url: string; lesson: any }) => void
  onUploadError?: (error: string) => void
  onUploadStart?: () => void
  onUploadComplete?: () => void
  maxSize?: number // bytes, e.g. 500MB = 500 * 1024 * 1024
  accept?: Record<string, string[]>
  className?: string
  initialVideo?: {
    url: string
    filename: string
  } | null
}

export function DirectVideoUploader({
  lessonId,
  onUploadSuccess,
  onUploadError,
  onUploadStart,
  onUploadComplete,
  maxSize = 3 * 1024 * 1024 * 1024, // Default 3GB
  accept = {
    'video/*': ['.mp4', '.webm', '.ogg'],
    'image/*': ['.jpg', '.jpeg', '.png', '.gif', '.webp'],
  },
  className,
  initialVideo = null,
}: DirectVideoUploaderProps) {
  const [status, setStatus] = useState<UploadStatus>(initialVideo ? 'success' : 'idle')
  const [progress, setProgress] = useState(0)
  const [uploadedVideo, setUploadedVideo] = useState<{ url: string; filename: string } | null>(
    initialVideo,
  )
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [statusMessage, setStatusMessage] = useState<string>('')
  const abortControllerRef = useRef<AbortController | null>(null)
  const xhrRef = useRef<XMLHttpRequest | null>(null)

  // コンポーネントのアンマウント時にアップロードをキャンセル
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort()
      }
      if (xhrRef.current) {
        xhrRef.current.abort()
      }
    }
  }, [])

  const handleDirectUpload = useCallback(
    async (acceptedFiles: File[]) => {
      const file = acceptedFiles[0]
      if (!file) return

      // 新しいAbortControllerを作成
      abortControllerRef.current = new AbortController()
      const signal = abortControllerRef.current.signal

      setStatus('generating-url')
      setProgress(0)
      setErrorMessage(null)
      setStatusMessage('アップロードURLを生成中...')

      onUploadStart?.()

      try {
        // デバッグログ
        console.log('[DirectVideoUploader] Starting upload for:', {
          filename: file.name,
          size: file.size,
          type: file.type,
          lessonId,
        })

        // Step 1: 署名付きURLを生成
        setStatusMessage('アップロードURLを取得中...')
        console.log(
          '[DirectVideoUploader] Fetching upload URL from:',
          `/api/lessons/${lessonId}/video/upload-url`,
        )

        const urlData = await api.post<{ url: string; blobName: string }>(
          `/api/lessons/${lessonId}/video/upload-url`,
          {
            filename: file.name,
            contentType: file.type,
          },
        )

        const { url: uploadUrl, blobName } = urlData

        console.log('[DirectVideoUploader] URL data received:', {
          hasUploadUrl: !!uploadUrl,
          blobName,
        })

        // Step 2: Azure Blob Storageに直接アップロード（XMLHttpRequestで進捗表示対応）
        setStatus('uploading')
        setStatusMessage('ファイルをアップロード中...')
        setProgress(0)

        console.log('[DirectVideoUploader] Uploading to Azure with XMLHttpRequest...')

        // XMLHttpRequestを使用してアップロード進捗を取得
        await new Promise<void>((resolve, reject) => {
          const xhr = new XMLHttpRequest()
          xhrRef.current = xhr

          // アップロード進捗イベント
          xhr.upload.addEventListener('progress', (e) => {
            if (e.lengthComputable) {
              // 0-85%の範囲で進捗を表示（残り15%は確認処理用）
              const percentComplete = Math.round((e.loaded / e.total) * 85)
              setProgress(percentComplete)
              console.log('[DirectVideoUploader] Upload progress:', `${percentComplete}%`)
            }
          })

          // 完了イベント
          xhr.addEventListener('load', () => {
            console.log('[DirectVideoUploader] Azure upload response:', {
              ok: xhr.status >= 200 && xhr.status < 300,
              status: xhr.status,
              statusText: xhr.statusText,
            })

            if (xhr.status >= 200 && xhr.status < 300) {
              resolve()
            } else {
              reject(new Error(`アップロードに失敗しました: ${xhr.status} ${xhr.statusText}`))
            }
          })

          // エラーイベント
          xhr.addEventListener('error', () => {
            reject(new Error('ネットワークエラーが発生しました'))
          })

          // キャンセルイベント
          xhr.addEventListener('abort', () => {
            reject(new Error('アップロードがキャンセルされました'))
          })

          // AbortControllerとの連携
          signal.addEventListener('abort', () => {
            xhr.abort()
          })

          // リクエストを送信
          xhr.open('PUT', uploadUrl)
          xhr.setRequestHeader('x-ms-blob-type', 'BlockBlob')
          xhr.setRequestHeader('Content-Type', file.type)
          xhr.send(file)
        })

        // Step 3: アップロード完了の確認とDB更新
        setStatus('confirming')
        setStatusMessage('アップロードを確認中...')
        setProgress(90)

        const confirmData = await api.post<{ url: string; lesson: unknown }>(
          `/api/lessons/${lessonId}/video/confirm`,
          { blobName },
        )

        // Success
        setStatus('success')
        setProgress(100)
        setStatusMessage('アップロード完了！')
        setUploadedVideo({ url: '', filename: file.name })

        onUploadSuccess?.(confirmData)
        onUploadComplete?.()
      } catch (error) {
        if (signal.aborted) {
          setStatus('idle')
          setStatusMessage('')
          return // ユーザーがキャンセルした場合はエラー扱いしない
        }

        console.error('[DirectVideoUploader] Upload error:', {
          error,
          message: error instanceof Error ? error.message : 'Unknown error',
          stack: error instanceof Error ? error.stack : undefined,
        })

        setStatus('error')
        setProgress(0)
        const msg = error instanceof Error ? error.message : 'アップロードに失敗しました'
        setErrorMessage(msg)
        setStatusMessage('')
        onUploadError?.(msg)
        onUploadComplete?.()
      }
    },
    [lessonId, onUploadSuccess, onUploadError, onUploadStart, onUploadComplete],
  )

  const onDrop = useCallback(
    (acceptedFiles: File[], fileRejections: FileRejection[], _event: DropEvent) => {
      if (fileRejections.length > 0) {
        const rejectionError = fileRejections[0].errors[0]
        let message = 'ファイルが拒否されました。'
        if (rejectionError.code === 'file-too-large') {
          message = `ファイルサイズが大きすぎます。最大 ${maxSize / 1024 / 1024}MB までです。`
        } else if (rejectionError.code === 'file-invalid-type') {
          message = 'サポートされていないファイル形式です。'
        }
        setStatus('error')
        setErrorMessage(message)
        onUploadError?.(message)
        onUploadComplete?.()
        return
      }
      if (acceptedFiles.length > 0) {
        handleDirectUpload(acceptedFiles)
      }
    },
    [handleDirectUpload, maxSize, onUploadError, onUploadComplete],
  )

  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    onDrop,
    accept,
    maxSize,
    multiple: false,
    preventDropOnDocument: true,
    disabled: status === 'uploading' || status === 'generating-url' || status === 'confirming',
    noClick: true, // divクリックでファイル選択ダイアログを開かない
    noKeyboard: true, // キーボードでも開かない
  })

  const resetState = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort()
    }
    if (xhrRef.current) {
      xhrRef.current.abort()
      xhrRef.current = null
    }
    setStatus('idle')
    setProgress(0)
    setUploadedVideo(null)
    setErrorMessage(null)
    setStatusMessage('')
  }

  const cancelUpload = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort()
    }
    if (xhrRef.current) {
      xhrRef.current.abort()
      xhrRef.current = null
    }
    setStatus('idle')
    setProgress(0)
    setStatusMessage('')
  }

  const renderContent = () => {
    switch (status) {
      case 'generating-url':
      case 'confirming':
        return (
          <div className="flex flex-col items-center justify-center text-center p-4">
            <Loader2 className="h-12 w-12 animate-spin text-primary mb-4" />
            <p className="text-sm font-medium">{statusMessage}</p>
            <Button
              onClick={cancelUpload}
              variant="outline"
              size="sm"
              className="mt-4"
              type="button"
            >
              キャンセル
            </Button>
          </div>
        )
      case 'uploading':
        return (
          <div className="flex flex-col items-center justify-center text-center p-4">
            <Loader2 className="h-12 w-12 animate-spin text-primary mb-4" />
            <p className="text-sm font-medium">{statusMessage}</p>
            <Progress value={progress} className="w-full mt-2 h-2" />
            <p className="text-xs text-muted-foreground mt-1">{progress}%</p>
            <Button
              onClick={cancelUpload}
              variant="outline"
              size="sm"
              className="mt-4"
              type="button"
            >
              キャンセル
            </Button>
          </div>
        )
      case 'success':
        return (
          <div className="flex flex-col items-center justify-center text-center p-4">
            <CheckCircle2 className="h-12 w-12 text-green-500 mb-4" />
            <p className="text-sm font-medium">アップロード完了！</p>
            {uploadedVideo && (
              <p className="text-xs text-muted-foreground truncate max-w-xs">
                {uploadedVideo.filename}
              </p>
            )}
            <div className="mt-4 flex gap-2">
              <Button onClick={resetState} variant="outline" size="sm" type="button">
                別の動画をアップロード
              </Button>
            </div>
          </div>
        )
      case 'error':
        return (
          <div className="flex flex-col items-center justify-center text-center p-4">
            <AlertTriangle className="h-12 w-12 text-destructive mb-4" />
            <p className="text-sm font-medium text-destructive">アップロード失敗</p>
            {errorMessage && (
              <p className="text-xs text-red-500 mt-1 max-w-xs text-wrap">{errorMessage}</p>
            )}
            <Button onClick={resetState} variant="outline" size="sm" className="mt-4" type="button">
              再試行
            </Button>
          </div>
        )
      default:
        return (
          <div className="flex flex-col items-center justify-center text-center p-4">
            <UploadCloud
              className={`h-12 w-12 mb-4 ${
                isDragActive ? 'text-primary' : 'text-muted-foreground'
              }`}
            />
            <p className="text-sm font-medium">ここに動画ファイルをドラッグ＆ドロップ</p>
            <p className="text-xs text-muted-foreground mb-2">または</p>
            <Button
              onClick={(e) => {
                e.stopPropagation() // バブリングを防ぐ
                open()
              }}
              variant="outline"
              size="sm"
              type="button" // フォーム送信を防ぐ
            >
              ファイルを選択
            </Button>
            <p className="text-xs text-muted-foreground mt-2">
              最大{' '}
              {maxSize >= 1024 * 1024 * 1024
                ? `${(maxSize / 1024 / 1024 / 1024).toFixed(0)}GB`
                : `${(maxSize / 1024 / 1024).toFixed(0)}MB`}
              までの {Object.values(accept).flat().join(', ')} ファイル
            </p>
            <p className="text-xs text-blue-600 mt-1">
              ✨ 直接アップロード - サーバーリソースを消費しません
            </p>
          </div>
        )
    }
  }

  return (
    <div
      {...getRootProps()}
      className={cn(
        'border-2 border-dashed rounded-lg cursor-pointer transition-colors',
        'focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2',
        isDragActive
          ? 'border-primary bg-accent'
          : 'border-border hover:border-muted-foreground/50',
        status === 'error' && 'border-destructive bg-destructive/10',
        status === 'success' && 'border-green-500 bg-green-500/10',
        (status === 'uploading' || status === 'generating-url' || status === 'confirming') &&
          'pointer-events-none opacity-75',
        className,
      )}
    >
      <input {...getInputProps()} />
      {renderContent()}
    </div>
  )
}
